import { isString, isObject } from 'lodash';
import { Currency, ServiceType } from '@ridi/event-client';
import { camelize } from '@ridi/object-case-converter';
import { createInAppBridge } from '@ridi-web/inappbridge';

export const inAppBridge = createInAppBridge();

inAppBridge.initialize();

export const getBookItem = book => ({
  service_type: ServiceType.RIDIBOOKS,
  item_id: book && book.id,
  item_name: book && book.title,
  item_episode_id: book && book.id,
  item_episode_name: book && book.title,
  item_category: book && book.category,
  item_parent_category: book && book.parent_category,
  item_genre: book && book.genre,
  price: book.price,
  quantity: 1,
});

const setFfid = ffid => {
  try {
    const { ridiEventClient } = window;
    if (ridiEventClient) {
      ridiEventClient.setFfid(ffid);
    }
  } catch (e) {
    /* ignore */
  }
};

const setUserId = () => {
  try {
    const { Ridi, ridiEventClient } = window;
    if (Ridi.globals.userIdx && ridiEventClient) {
      ridiEventClient.setUId(Ridi.globals.userIdx);
    }
  } catch (e) {
    /* ignore */
  }
};

export const selectReview = () => {
  try {
    const { ridiEventClient } = window;
    if (ridiEventClient) {
      setUserId();
      ridiEventClient.sendEvent(
        'BookDetailSelectReview',
        undefined,
        new Date(),
      );
    }
  } catch (e) {
    /* ignore */
  }
};

export const beginCheckout = () => {
  try {
    const { context, ridiEventClient } = window;
    const { isRidiApp } = window.Ridi.globals;

    if (context.order.detail.books) {
      const { books } = context.order.detail;
      const items = books.map(getBookItem);

      if (isRidiApp) {
        inAppBridge.log.beginCheckout({
          items: items.map(item => camelize(item)),
        });
      } else if (ridiEventClient) {
        setUserId();
        ridiEventClient.sendBeginCheckout(items);
      }
    }
  } catch (e) {
    /* ignore */
  }
};

export const addPaymentInfo = value => {
  try {
    const { context, ridiEventClient } = window;
    if (ridiEventClient) {
      if (context.order.detail.books) {
        const { books } = context.order.detail;
        const items = books.map(getBookItem);
        setUserId();
        ridiEventClient.sendAddPaymentInfo({
          currency: Currency.KRW,
          items,
          value,
        });
      }
    }
  } catch (e) {
    /* ignore */
  }
};

export const getPurchaseTrackingParams = trackingInfo => {
  let ret = {};
  if (trackingInfo) {
    try {
      const {
        _rdt_sid: section_id,
        _rdt_idx: section_item_index_raw,
        _rdt_ref: section_referrer,
        _rdt_loc: section_page,
        _rdt_arg: section_arg,
      } = JSON.parse(trackingInfo);

      const section_item_index = Number(section_item_index_raw);
      ret = {
        section_id,
        section_item_index: Number.isNaN(section_item_index)
          ? undefined
          : section_item_index,
        section_referrer,
        section_page,
        section_arg,
      };
      Object.keys(ret).forEach(
        key => ret[key] === undefined && delete ret[key],
      );
      // eslint-disable-next-line no-empty
    } catch (e) {}
  }
  return ret;
};

export const purchase = () => {
  try {
    const { Ridi, ridiEventClient } = window;
    const { receipt, trackingInfo } = Ridi.Context;
    const { isRidiApp } = Ridi.globals;

    // https://app.asana.com/0/1193106288044291/1202180156767679/f
    // 가격이 0이거나 무료이용권으로 무료로 결제한 경우에는 구매가 아니라 이용으로 마케팅 성과 측정시 기준을 통일
    if (receipt && receipt.sum > 0) {
      const { books, t_id, used_coupon_serial_numbers } = receipt;
      const items = books.map(getBookItem);

      if (ridiEventClient) {
        setUserId();
        ridiEventClient.sendPurchase(t_id, {
          currency: Currency.KRW,
          items,
          value: receipt.sum,
          ...getPurchaseTrackingParams(trackingInfo),
          coupon:
            Array.isArray(used_coupon_serial_numbers)
            && used_coupon_serial_numbers.length
              ? used_coupon_serial_numbers.join(',')
              : undefined,
        });
      } else if (isRidiApp) {
        inAppBridge.log.purchase({
          currency: Currency.KRW,
          transactionId: t_id,
          value: receipt.sum,
          items: items.map(item => camelize(item)),
        });
      }
    }
  } catch (e) {
    /* ignore */
  }
};

const getBookItemByDetailBook = book => getBookItem({
  ...book,
  is_series:
      typeof book.is_serial !== 'boolean'
        ? book.is_series === '1'
        : book.is_series,
});

export const viewItem = options => {
  const { trackingInfo } = options || {};
  const { section_id, section_item_index } = trackingInfo || {};

  try {
    const { bookDetail, ridiEventClient } = window;
    if (ridiEventClient && bookDetail) {
      const items = [getBookItemByDetailBook(bookDetail)];
      setUserId();
      if (section_id) {
        ridiEventClient.sendViewItem(
          items.map(item => ({
            ...item,
            item_list_id: section_id,
            item_list_index: section_item_index,
          })),
        );
      } else {
        ridiEventClient.sendViewItem(items);
      }
    }
  } catch (e) {
    /* ignore */
  }
};

export const preference = isEnroll => {
  try {
    const { bookDetail, ridiEventClient } = window;
    if (ridiEventClient && bookDetail) {
      const items = [getBookItemByDetailBook(bookDetail)];
      setUserId();
      if (isEnroll) {
        ridiEventClient.sendEnrollPreference(items);
      } else {
        ridiEventClient.sendUnenrollPreference(items);
      }
    }
  } catch (e) {
    /* ignore */
  }
};

export const newBookNotification = isEnroll => {
  try {
    const { bookDetail, ridiEventClient } = window;
    if (ridiEventClient) {
      // /author/{id} 에서 호출되는 경우 bookDetail이 존재하지 않는다. 이 경우 URI로 유추한다.
      const items = bookDetail ? [getBookItemByDetailBook(bookDetail)] : [];
      setUserId();
      if (isEnroll) {
        ridiEventClient.sendEnrollNewBookNotification(items);
      } else {
        ridiEventClient.sendUnenrollNewBookNotification(items);
      }
    }
  } catch (e) {
    /* ignore */
  }
};

export const signUpFinished = () => {
  try {
    const { ridiEventClient } = window;
    if (ridiEventClient) {
      setUserId();
      ridiEventClient.sendSignUp();
    }
  } catch (e) {
    /* ignore */
  }
};

export const beginCheckoutInSerialPopup = (bookId, bookTitle) => {
  try {
    const { bookDetail, ridiEventClient } = window;
    if (ridiEventClient && bookDetail) {
      const book = {
        ...getBookItemByDetailBook(bookDetail),
        item_episode_id: bookId,
        item_episode_name: bookTitle,
      };
      const items = [book];
      setUserId();
      ridiEventClient.sendBeginCheckout(items);
    }
  } catch (e) {
    /* ignore */
  }
};

export const purchaseInSerialPopup = (
  bookId,
  bookTitle,
  sum,
  t_id,
  trackingInfo,
) => {
  try {
    const { bookDetail, ridiEventClient } = window;
    if (ridiEventClient && bookDetail) {
      const book = {
        ...getBookItemByDetailBook(bookDetail),
        item_episode_id: bookId,
        item_episode_name: bookTitle,
      };
      const items = [book];
      setUserId();
      ridiEventClient.sendPurchase(t_id, {
        currency: Currency.KRW,
        items,
        value: sum,
        ...getPurchaseTrackingParams(trackingInfo),
      });
    }
  } catch (e) {
    /* ignore */
  }
};

export const sendUserAttribute = (key, attr, ts) => {
  const { bookDetail, ridiEventClient } = window;
  if (
    ridiEventClient
    && typeof ridiEventClient.sendUserAttribute === 'function'
  ) {
    const attrVal = typeof attr === 'function' ? attr(bookDetail) : attr;
    ridiEventClient.sendUserAttribute(key, attrVal, ts);
  }
};

const getBookItemsWithDetailByBookIds = bookIds => {
  const { bookDetail } = window;

  const bookItem = bookDetail ? getBookItemByDetailBook(bookDetail) : {};
  const items = bookIds.map(id => (bookItem.item_id === id ? bookItem : { item_id: id }));

  return items;
};

export const sendAddToCart = bookIds => {
  const { ridiEventClient } = window;
  setUserId();
  ridiEventClient.sendAddToCart(getBookItemsWithDetailByBookIds(bookIds));
};

export const sendRemoveFromCart = bookIds => {
  const { ridiEventClient } = window;
  if (ridiEventClient) {
    setUserId();
    ridiEventClient.sendRemoveFromCart(
      getBookItemsWithDetailByBookIds(bookIds),
    );
  }
};

export const sendCartToWishlist = bookIds => {
  const { ridiEventClient } = window;
  if (ridiEventClient) {
    setUserId();
    ridiEventClient.sendCartToWishlist(
      getBookItemsWithDetailByBookIds(bookIds),
    );
  }
};

export const sendWishlistToCart = bookIds => {
  const { ridiEventClient } = window;
  if (ridiEventClient) {
    setUserId();
    ridiEventClient.sendWishlistToCart(
      getBookItemsWithDetailByBookIds(bookIds),
    );
  }
};

export const sendAddToWish = bookIds => {
  const { ridiEventClient } = window;
  if (ridiEventClient) {
    setUserId();
    ridiEventClient.sendAddToWish(getBookItemsWithDetailByBookIds(bookIds));
  }
};

export const sendRemoveFromWish = bookIds => {
  const { ridiEventClient } = window;
  setUserId();
  ridiEventClient.sendRemoveFromWish(getBookItemsWithDetailByBookIds(bookIds));
};

/**
 * @param {string|object} event
 * @param {any} params
 */
export const sendCustomEvent = (event, params) => {
  const { ridiEventClient } = window;
  const { isRidiApp } = window.Ridi.globals;

  if (isRidiApp && isObject(event)) {
    inAppBridge.log.event(event.screenName, event.action, event.target, params);
  } else if (ridiEventClient) {
    const eventName = isString(event)
      ? event
      : isObject(event)
        ? `${event.screenName}_${event.action}_${event.target}`
        : '';
    setUserId();
    ridiEventClient.sendCustomEvent(eventName, params);
  }
};

export const sendInAppPageView = (shouldRefresh = true) => {
  const {
    isAndroid, isIos, isRidiApp, userIdx,
  } = window.Ridi.globals;

  if (!isRidiApp) {
    return;
  }

  if (!isAndroid && !isIos) {
    return;
  }

  const send = () => {
    inAppBridge.log.pageView({
      genre: window.event_genre_name,
      eventId: window.event_id,
      eventName: window.event_title,
    });
  };

  inAppBridge.addListener('webViewResume', () => {
    send();
    if (shouldRefresh) {
      window.location.reload();
    }
  });
  send();
};

export function parseSearchParams(defaults = {}) {
  const searchParams = new URLSearchParams(window.location.search);
  return Object.keys(defaults).reduce((params, key) => {
    params[key] = searchParams.get(key) || defaults[key];
    return params;
  }, {});
}

export function observeElements(selector, handleViewEvent, threshold = 0.5) {
  const observer = new IntersectionObserver(
    entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const $el = $(entry.target);
          const index = $el.index();
          handleViewEvent($el, index);
          observer.unobserve(entry.target);
        }
      });
    },
    { threshold },
  );

  $(selector).each((_, el) => observer.observe(el));
}

export function bindClickEvent($container, childSelector, handleSelectEvent) {
  $container.on('click', childSelector, function () {
    const $el = $(this).closest($container);
    const index = $el.index();
    handleSelectEvent($el, index);
  });
}



// WEBPACK FOOTER //
// ./util/eventClient.js